import type { ExamId, IsoDateTime, QuestionId } from './brands.ts';
import type { Outcome } from './session.ts';

/**
 * What happened the last few times this question was attempted.
 *
 * The point of the whole feature: "give me the ones I keep getting wrong" is the single most
 * useful thing a practice tool can do, and it cannot be answered from one session.
 *
 * Deliberately tiny. A record holds counts and one timestamp — never the question text, never the
 * answer given, never the exam file. What is kept is what is needed to filter, and nothing that
 * would make the store worth reading for any other purpose.
 */
export interface QuestionAttempts {
  /** Times this question has been scored at the end of a session. */
  readonly seen: number;
  /** Of those, how many were wrong. Partial credit counts as wrong: it was not right. */
  readonly incorrect: number;
  readonly lastOutcome: Outcome;
  readonly lastAt: IsoDateTime;
}

/** Attempts for one exam, by question id. */
export type ExamHistory = Readonly<Record<string, QuestionAttempts>>;

/** Every exam this device has a history for, by exam id. */
export type AttemptHistory = Readonly<Record<string, ExamHistory>>;

export const NO_HISTORY: ExamHistory = {};

/**
 * Whether an outcome counts against the candidate.
 *
 * `ungraded` does not: the app could not mark it, and treating "we do not know" as "you were
 * wrong" would fill the review list with essays. Unanswered does not either — skipping is a
 * different problem from misunderstanding, and a session cut short would otherwise poison the
 * history of every question it never reached.
 */
export function countsAsIncorrect(outcome: Outcome): boolean {
  return outcome === 'incorrect' || outcome === 'partial';
}

/** How many times a question has been answered incorrectly, for a history that may not know it. */
export function incorrectCount(history: ExamHistory, questionId: QuestionId): number {
  return history[questionId]?.incorrect ?? 0;
}

/**
 * Folds one finished session into the history for its exam.
 *
 * Pure, so the merge is testable without a browser and the store stays a thin wrapper. Keyed by
 * `examId` rather than by content hash: a corrected file is still the same exam, and losing the
 * history every time an author fixes a typo would make the feature useless.
 */
export function recordAttempts(
  history: ExamHistory,
  outcomes: readonly { readonly questionId: QuestionId; readonly outcome: Outcome }[],
  at: IsoDateTime,
): ExamHistory {
  const next: Record<string, QuestionAttempts> = { ...history };

  for (const { questionId, outcome } of outcomes) {
    // A question that was never reached tells us nothing about whether it is understood.
    if (outcome === 'unanswered') {
      continue;
    }
    const previous = next[questionId];
    next[questionId] = {
      seen: (previous?.seen ?? 0) + 1,
      incorrect: (previous?.incorrect ?? 0) + (countsAsIncorrect(outcome) ? 1 : 0),
      lastOutcome: outcome,
      lastAt: at,
    };
  }

  return next;
}

/** The exams a history knows about, most recently attempted first. */
export function examsWithHistory(history: AttemptHistory): readonly ExamId[] {
  return Object.keys(history).sort() as ExamId[];
}
